import type { ReactNode } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import ArticleHero from '../../../../components/news/ArticleHero';
import PullQuote from '../../../../components/news/PullQuote';
import KeyPointsBox from '../../../../components/news/KeyPointsBox';
import CommentBox from '../../../../components/news/CommentBox';
import SourceCard from '../../../../components/news/SourceCard';
import RelatedCards from '../../../../components/news/RelatedCards';

/* ---- In-article inline parts (editorial layout; no section bands) ---- */

function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}

function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        Source: {media} (<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>)
      </figcaption>
    </figure>
  );
}

function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}

const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function WeaveRoboticsIsaacRobotButlerPageEN() {
  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'News', href: '/en/news' },
    { label: 'Weave Robotics Isaac Home Robot Butler Announced' }
  ];

  const article = {
    category: 'Technology & Future Trends',
    date: 'September 9, 2024',
    title: 'Weave Robotics Isaac Home Robot Butler Announced',
    subtitle: '$59,000 Home Automation Robot Shows Future of Butler Services',
    heroImage: '/images/generated/jba-fs-139-news_weave-robotics-isaac-robot-butler.webp',
    lead: 'Weave Robotics\' announced home robot "Isaac" is a versatile personal robot priced at $59,000 that autonomously performs various household tasks.\n\nWith initial 30 units scheduled for shipment in fall 2025, it shows the butler service concept being realized as a concrete commercial product.',
    comment: 'Weave Robotics\' "Isaac" announcement is an important case showing the cultural significance of robot technology adopting the "butler" concept.\n\nThe $59,000 pricing proves that butler services are recognized as premium services in the technology field as well.\n\nHowever, there are clear differences between household tasks that technology can substitute and the emotional intelligence, cultural understanding, and flexible response that human butlers provide.\n\nRobot technology development rather highlights the indispensability of human butlers—personalization, trust relationship building, and adaptation to complex situations.\n\nTechnology and traditional butler services are in a complementary relationship, and we will capture this as an opportunity to reassess the value of human services in the Japanese market and work toward establishing more advanced butler services.',
    related: [
      { to: '/en/news/apple-robot-butler', image: '/images/generated/jba-fs-en-024-news_weave-robotics-isaac-robot-butl.webp', aspect: 'aspect-[3/4]', alt: 'Related article: Apple Robot Butler Development Project', title: 'Apple Robot Butler Development Project', desc: "Butler robot development by world's largest tech company" },
      { to: '/en/news/ai-hospitality-revolution', image: '/images/generated/jba-fs-en-025-news_weave-robotics-isaac-robot-butl.webp', aspect: 'aspect-[4/3]', alt: 'Related article: AI Hospitality Revolution: 5 Transformations', title: 'AI Hospitality Revolution: 5 Transformations', desc: 'The future of butler services opened by AI' }
    ]
  };

  const sources = [
    { label: 'Source', value: 'The Rundown AI', link: false },
    { label: 'Article URL', value: 'https://www.therundown.ai/p/futuristic-robot-butlers-weave-2025', link: true },
    { label: 'Pricing & Shipping (Isaac 1)', value: 'https://thenextweb.com/news/weave-robotics-isaac-1-home-robot', link: true },
    { label: 'Manufacturer Official Site', value: 'https://www.weaverobotics.com/', link: true },
    { label: 'Access Date', value: 'July 2026', link: false }
  ];

  const toc = [
    { id: 's-product', label: 'Product Overview and Key Features' },
    { id: 's-company', label: 'About Weave Robotics' },
    { id: 's-pricing', label: 'Pricing and Market Strategy' },
    { id: 's-competition', label: 'Competitive Landscape and Market Heating' },
    { id: 's-isaac0', label: 'Isaac 0 and Subsequent Developments' },
    { id: 's-realization', label: 'Technological Realization of Butler Service' }
  ];

  return (
    <Layout>
      <title>Weave Robotics Isaac Home Robot Butler Announced | Japan Butler Association</title>
      <meta name="description" content="Weave Robotics unveils Isaac, a $59,000 versatile home robot that autonomously handles household tasks, with initial 30 units scheduled to ship in fall 2025." />
      <link rel="canonical" href="https://butler-association.jp/en/news/weave-robotics-isaac-robot-butler" />
      <link rel="alternate" hrefLang="ja" href="https://butler-association.jp/news/weave-robotics-isaac-robot-butler" />
      <link rel="alternate" hrefLang="en" href="https://butler-association.jp/en/news/weave-robotics-isaac-robot-butler" />

      <ArticleHero eyebrow="News / Technology" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <div className="space-y-4">
              {article.lead.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean).map((s, i) => (
                <p key={i} className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{s}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="Article Highlights"
              points={[
                'Weave\'s home robot "Isaac" is a versatile personal robot that autonomously tidies up, folds laundry and organizes spaces, operated by voice, text or app',
                'At announcement the price was $59,000 (installments of $1,385/month for 48 months), with a $1,000 deposit and an initial lot of 30 U.S. customers, scheduled to ship in fall 2025',
                'Competitors including Tesla Optimus, 1X NEO Beta and China\'s AGIBOT are entering the market, heating up the home robot butler space'
              ]}
            />

            {/* 01: Product Overview (existing body, unchanged; source shot inserted mid-section to avoid a text wall) */}
            <SectionHeading id="s-product" en="Product">Product Overview and Key Features</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Weave's announced "Isaac" is a versatile personal robot manufactured in California, designed to help with various household tasks. This robot can autonomously tidy up, fold laundry, and organize spaces.</p>
              <div className="space-y-5">
                <p className={P}>Particularly notable functions include feeding pets and tidying messy rooms, all of which can be performed while owners are away.</p>
                <p className={P}>This technologically realizes the concept of "household management during absence" traditionally provided by butler services.</p>
              </div>
            </div>
            <SourceShot
              src="/images/news-sources/weave-robotics-isaac-robot-butler-therundown.jpg"
              alt="Screenshot of The Rundown AI article covering Weave Robotics' home robot butler Isaac"
              media="The Rundown AI"
              url="https://www.therundown.ai/p/futuristic-robot-butlers-weave-2025"
            />
            <div className="space-y-6">
              <p className={P}>Operation responds to voice or text commands and can be programmed via app. For privacy considerations, the camera Isaac carries can be folded and stored in an attached case when not in use.</p>
            </div>

            {/* Augmented: About the company */}
            <SectionHeading id="s-company" en="The Company">About Weave Robotics</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Weave Robotics is a startup founded by Evan and Kaan, both alumni of Y Combinator, developing practical home robots. The company positions its products as "the robot we built for our own homes."</p>
              <p className={P}>Designing on the premise that the founders themselves use it in daily life reflects the company's orientation toward a practical machine that withstands everyday chores, rather than a lab prototype.</p>
            </div>

            {/* 02: Pricing (existing body, unchanged; pull quote inserted to avoid a text wall) */}
            <SectionHeading id="s-pricing" en="Pricing">Pricing and Market Strategy</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Isaac's pricing is set at a premium $59,000, with installment payment options of $1,385/month for 48 months. The reservation deposit is $1,000, with the initial lot limited to 30 customers in the United States.</p>
            </div>
            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="A $59,000 home automation robot points to the future of butler services." cite="Weave Robotics Isaac" />
            </div>
            <div className="space-y-6">
              <div className="space-y-5">
                <p className={P}>This pricing clearly positions the robot butler as a premium service.</p>
                <p className={P}>The $59,000 price is equivalent to employing a highly-paid professional butler for several years, showing that technological butler service substitution targets the high-value market.</p>
              </div>
              <p className={P}>Shipment to first customers is scheduled for fall 2025, with a strategy of starting with limited initial deployment to ensure product quality and customer satisfaction while entering the market.</p>
            </div>

            {/* 03: Competition (existing body, unchanged; key points box inserted to avoid a text wall) */}
            <SectionHeading id="s-competition" en="Competition">Competitive Landscape and Market Heating</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Competition for affordable, intelligent personal robot assistants continues to heat up, with these robots expected to be seen in homes by 2025. Major competitors include:</p>
              <p className={P}>Tesla Optimus is a humanoid robot developed by Tesla, known for electric vehicles, aiming for wide applications from manufacturing to home use. 1X NEO Beta is a humanoid robot developed by a Norwegian company, featuring more human-like movements.</p>
            </div>
            <div className="my-6">
              <KeyPointsBox
                title="Major Competing Robots"
                points={[
                  'Tesla Optimus — a humanoid robot from Tesla, aiming for wide applications from manufacturing to home use',
                  '1X NEO Beta — a humanoid robot developed by a Norwegian company, featuring more human-like movements',
                  "AGIBOT (China) — entering similar markets and advancing deployment across Asia"
                ]}
              />
            </div>
            <div className="space-y-6">
              <p className={P}>China's AGIBOT is also entering similar markets, advancing deployment in Asian markets. These companies simultaneously entering the home robot butler market shows this field is not just a future dream but an imminent reality.</p>
            </div>

            {/* Augmented: Isaac 0 and subsequent developments */}
            <SectionHeading id="s-isaac0" en="Isaac 0">Isaac 0 and Subsequent Developments</SectionHeading>
            <div className="space-y-6">
              <div className="space-y-5">
                <p className={P}>Weave subsequently began shipping "Isaac 0," a stationary laundry-folding predecessor model, across California.</p>
                <p className={P}>According to its official site, Isaac 0 folds over <Stat>1,000</Stat> lbs of laundry per week and has accumulated more than <Stat>2,000</Stat> hours of real-world field operation.</p>
              </div>
              <p className={P}>The company has also unveiled a next-generation model, "Isaac 1," equipped with wheels and arms, progressively expanding its home robot lineup. Steady, data-driven product evolution is a hallmark of the company's approach.</p>
            </div>

            {/* 04: Technological Realization (existing body, unchanged; source shot inserted to avoid a text wall) */}
            <SectionHeading id="s-realization" en="Realization">Technological Realization of Butler Service Concept</SectionHeading>
            <div className="space-y-6">
              <p className={P}>The most important significance of this announcement is that a robot named "Butler" attempts to technologically realize the traditional butler service concept—household management, task automation, and quality of life improvement.</p>
              <p className={P}>The article concludes with "excited about never having to fold laundry again," expressing expectations for technology realizing "liberation from daily tasks"—the essential value of butler services.</p>
            </div>
            <SourceShot
              src="/images/news-sources/weave-robotics-isaac-robot-butler-weaverobotics.jpg"
              alt="Screenshot of the Weave Robotics official website homepage, presenting the current Isaac 1 model"
              media="Weave Robotics official site"
              url="https://www.weaverobotics.com/"
            />
            <div className="space-y-6">
              <div className="space-y-5">
                <p className={P}>The rapid realization of the home robot market has the potential to significantly change the future of household labor.</p>
                <p className={P}>However, elements that human butlers provide—emotional intelligence, cultural understanding, and flexible response—may be re-recognized as values difficult to substitute with technology.</p>
              </div>
            </div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="Contents" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">Announced</dt><dd className="text-[#333] mt-0.5">September 9, 2024</dd></div>
                  <div><dt className="text-[#8f7a56]">Maker</dt><dd className="text-[#333] mt-0.5">Weave Robotics (YC)</dd></div>
                  <div><dt className="text-[#8f7a56]">Isaac Price</dt><dd className="text-[#333] mt-0.5">$59,000</dd></div>
                  <div><dt className="text-[#8f7a56]">Category</dt><dd className="text-[#333] mt-0.5">{article.category}</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="Comment from Japan Butler Association" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="Source" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="Related Articles" items={article.related} readLabel="Read" backHref="/en/news" backLabel="Back to News List" />
    </Layout>
  );
}
